import React, { useState, useEffect } from 'react';
import { Button, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import PieInfoWidget from 'components/Widgets/PieInfoWidgetPrimary';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import { useTheme } from '@mui/material/styles';
import DashboardCard from 'components/Widgets/Distribution Card';
import DoughnutChart from 'components/Widgets/DoughnutChart';
import PieChart from 'components/Widgets/PieChart';
import GaugeChart from 'components/Widgets/GaugeChart';
import ThreeDPieChart from 'components/Widgets/ThreeDPieChart';

const azureServiceData = [
  {
    name: 'AKS (Compute)',
    value: 39,
  },
  {
    name: 'Storage',
    value: 36,
  },
  {
    name: 'Databricks',
    value: 5,
  },
  {
    name: 'Data Factory',
    value: 4,
  },
];

const azureServiceColors = [
  '#0037F9',
  '#FF928A',
  '#00D1FF',
  '#00628B',
];

export default function PieChartsPage() {
  const theme = useTheme();
  const colors = theme.palette;
  const navigate = useNavigate();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [widgets, setWidgets] = useState<any[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const sampleMetricPieComboData = [
    { name: 'Item-A', value: 225, formattedName: '225' },
    { name: 'Item-B', value: 228, formattedName: '228' },
    { name: 'Item-C', value: 228, formattedName: '200' },
  ];

  // -------------------------
  // Sample API Response
  // -------------------------
  const BasicPieChartSampleResponse = {
    pie: [
      {
        CountA: 42,
        CountB: 18,
        totalCount: 60,
        currentMonthCount: 60,
        lastMonthCount: 50,
      },
    ],
  };

  const sampleSeriesData = [
    {
      name: 'PowerBI',
      formattedName: 'PowerBI',
      value: 1420,
      percentage: '17%',
      color: '#00B3B3',
    },
    {
      name: 'Databricks',
      formattedName: 'Databricks',
      value: 1180,
      percentage: '14%',
      color: '#00C389',
    },
    {
      name: 'Teradata',
      formattedName: 'Teradata',
      value: 1650,
      percentage: '20%',
      color: '#AFC6F5',
    },
    {
      name: 'Tableau',
      formattedName: 'Tableau',
      value: 1320,
      percentage: '16%',
      color: '#7FA2E6',
    },
    {
      name: 'Presto',
      formattedName: 'Presto',
      value: 980,
      percentage: '12%',
      color: '#63B32E',
    },
    {
      name: 'EDF Pipelines',
      formattedName: 'EDF Pipelines',
      value: 860,
      percentage: '10%',
      color: '#FF8A1E',
    },
    {
      name: 'SAP',
      formattedName: 'SAP',
      value: 620,
      percentage: '7%',
      color: '#E91E63',
    },
    {
      name: 'Others',
      formattedName: 'Others',
      value: 381,
      percentage: '4%',
      color: '#F8BBD0',
    },
  ];

  const samplePieSeriesData = [
    {
      name: 'PowerBI',
      formattedName: 'PowerBI',
      value: 1420,
      percentage: '17%',
      color: '#00B3B3',
    },
    {
      name: 'Databricks',
      formattedName: 'Databricks',
      value: 1180,
      percentage: '14%',
      color: '#00C389',
    },
    {
      name: 'Teradata',
      formattedName: 'Teradata',
      value: 1650,
      percentage: '20%',
      color: '#AFC6F5',
    },
    {
      name: 'Tableau',
      formattedName: 'Tableau',
      value: 1320,
      percentage: '16%',
      color: '#7FA2E6',
    },
  ];

  const calculateChange = (current: number, last: number): number => {
    if (last !== 0) {
      return ((current - last) / last) * 100;
    }

    return current > 0 ? 100 : 0;
  };

  // -------------------------
  // Transform API → Widget Format
  // -------------------------
  const transformData = (response: any) => {
    const data = response || {};
    const widgetData: any[] = [];

    if (Array.isArray(data.pie) && data.pie.length > 0) {
      const item = data.pie[0];

      const change = calculateChange(item.currentMonthCount, item.lastMonthCount);

      const chartData = [
        {
          name: 'Item-A',
          value: item.CountA,
          formattedName: String(item.CountA || 0),
        },
        {
          name: 'Item-B',
          value: item.CountB,
          formattedName: String(item.CountB || 0),
        },
      ];

      widgetData.push({
        title: 'Basic Pie Chart',
        value: item.totalCount,
        data: chartData,
        change: Math.abs(Number(change.toFixed(2))),
        changeType: item.currentMonthCount > item.lastMonthCount ? 'increase' : 'decrease',
        colorMap: {
          'Item-A': colors.widget.colors.color1,
          'Item-B': colors.widget.colors.color2,
        },
      });
    }

    return widgetData;
  };

  // -------------------------
  // Load Data on Mount
  // -------------------------
  useEffect(() => {
    setChartLoading(true);

    setTimeout(() => {
      const processed = transformData(BasicPieChartSampleResponse);
      setWidgets(processed);
      setChartLoading(false);
    }, 600);
  }, []);

  const pieColorMapping = {
    ADLS: '#01C008',
    Teradata: '#299BFF',
    'Azure SQL': '#FE2626',
    Synapse: '#F4793B',
    Cosmos: '#FFA200',
  };

  const donutColorMapping = {
    Azure: '#01C008',
    Synapse: '#299BFF',
    Teradata: '#FE2626',
  };

  return (
    <PageWrapper>
      <PageHeader title="Pie Charts Demo">
        <Button variant="pepwiseSecondary" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </PageHeader>

      <Grid container spacing={3} mt={2}>
        {widgets.map((widget, index) => (
          <Grid
            item
            xs={12}
            sm={expandedIndex === index ? 8 : 6}
            md={expandedIndex === index ? 8 : 4}
            key={`${widget.title}-${widget.value}`}
          >
            <PieInfoWidget
              title={widget.title}
              value={widget.value}
              change={widget.change}
              changeType={widget.changeType}
              isLoading={chartLoading}
              seriesData={widget.data}
              showDollar={false}
              showPercentage={false}
              colorMapping={widget.colorMap}
              hasTooltip={true}
              tooltipLabel="This widget displays a quick visual breakdown of data using an interactive pie chart. It shows the total, monthly change, and allows users to click slices for more details. The chart adjusts automatically to different screen sizes, supports expand/collapse mode, and shows a clean “No data” state when needed. It can also display both percentage and absolute values on each slice. Optional features include tooltips"
              enableSectorCut={false}
              setEnableSectorCut={false}
              isExpanded={expandedIndex === index}
              onSliceClick={(slice) => {
                console.log('Clicked Slice:', slice);
              }}
              onToggleExpand={() => setExpandedIndex((prev) => (prev === index ? null : index))}
              hasExpand={true}
            />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} sm={isExpanded ? 12 : 6} md={isExpanded ? 8 : 4}>
          <DashboardCard
            title="Size"
            hasTooltip={true}
            tooltipLabel="This card displays a configurable pie or donut distribution with tabs, legends, allowing you to compare datasets interactively."
            showUnits={true}
            tabs={[
              {
                key: 'stored',
                label: 'Data Stored',
                showUnitToggle: true,
                tooltip:
                  'This tab shows the distribution of data stored across different platforms.',
                charts: [
                  {
                    chartType: 'pie',
                    data: [
                      { name: 'ADLS', value: 102088012 },
                      { name: 'Teradata', value: 52198367 },
                      { name: 'Azure SQL', value: 41531345 },
                      { name: 'Synapse', value: 42986456 },
                      { name: 'Cosmos', value: 46234523 },
                    ],
                    colorMapping: pieColorMapping,
                    onSliceClick: (item) => console.log('Pie slice clicked:', item),
                  },
                ],
              },
              {
                key: 'trend',
                label: 'Data Trends',
                showUnitToggle: false,
                tooltip: 'This tab illustrates the trends in data usage over time.',
                charts: [
                  {
                    chartType: 'donut',
                    title: 'Donut Chart',
                    data: [
                      { name: 'Azure', value: 20 },
                      { name: 'Synapse', value: 12 },
                      { name: 'Teradata', value: 10 },
                    ],
                    colorMapping: donutColorMapping,
                    onSliceClick: (item) => console.log('Donut slice clicked:', item),
                  },
                ],
              },
            ]}
            isExpanded={isExpanded}
            onToggleExpand={() => setIsExpanded((prev) => !prev)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <PieInfoWidget
            title="Combination of Metric & Pie Chart"
            value={653}
            isLoading={false}
            seriesData={sampleMetricPieComboData}
            showDollar={false}
            showPercentage={false}
            hasTooltip={true}
            tooltipLabel="This widget displays a quick visual breakdown of data using an interactive pie chart. It shows the total, and allows users to click slices for more details."
            enableSectorCut={false}
            setEnableSectorCut={false}
            onSliceClick={(slice) => {
              console.log('Clicked Slice:', slice);
            }}
            hasExpand={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <DoughnutChart
            isLoading={false}
            title="Incident Intake Channel Metrics"
            totalText={`{small|Total}\n{large|8411}`}
            hasTooltip
            tooltipLabel="Shows the distribution of incidents received across different intake
                          channels such as BI tools, data platforms, and pipelines."
            seriesData={sampleSeriesData}
            dataLabel={false}
            legendLabel={true}
            hoursFormat={false}
            chartSize="250px"
            handleEvent={(event: any, title: any) => {
              console.log('Clicked segment:', event);
              console.log('Chart title:', title);
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <PieChart
            isLoading={false}
            title="Incident Intake Channel Metrics"
            hasTooltip
            tooltipLabel="Shows the distribution of incidents received across different intake
                          channels such as BI tools, data platforms, and pipelines."
            seriesData={samplePieSeriesData}
            dataLabel={true}
            legendLabel={true}
            chartSize="250px"
            handleEvent={(event: any, title: any) => {
              console.log('Clicked segment:', event);
              console.log('Chart title:', title);
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <GaugeChart
            title="Knowledge Base Articles"
            value={99}
            isLoading={false}
            hasTooltip
            tooltipLabel="Displays the percentage of knowledge base articles"
            chartSize="250px"
            handleEvent={(event: any, title: any) => {
              console.log('Gauge event:', event);
              console.log('Chart title:', title);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ThreeDPieChart
            title="Azure Service Cost Dissection"
            seriesData={azureServiceData}
            pieColors={azureServiceColors}
            showDataLabels
            showLegend
            showTooltip
            chartHeight={420}
            // pieRadius="65%"
            // center={['50%', '43%']}
            // depth={18}
            decimalPlaces={2}
          />
        </Grid>
      </Grid>
    </PageWrapper>
  );
}
